import {
  LoadableTrackingContext,
  TrackingPageContextValue,
} from '@aboutyou/legacy-shared/src/tracking/context/PageTrackingContext'
import React, {
  FC,
  MutableRefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type PageTrackingContextChangeListener = (
  context: LoadableTrackingContext<TrackingPageContextValue>,
) => void

export type AsyncTrackingPageContextValue = {
  addChangeListener(
    listener: PageTrackingContextChangeListener,
    getInitialState?: boolean,
  ): void
  removeChangeListener(listener: PageTrackingContextChangeListener): void
  update: PageTrackingContextChangeListener
  contextRef: MutableRefObject<LoadableTrackingContext<TrackingPageContextValue> | null>
}

const noop = () => {}
const AsyncPageTrackingContext = createContext<AsyncTrackingPageContextValue>({
  addChangeListener: noop,
  removeChangeListener: noop,
  update: noop,
  contextRef: { current: null },
})

/**
 * the AsyncPageTrackingContextProvider is used to share
 * the tracking page context to components rendered outside
 * of the actual page layout.
 * It should not be used for tracking events like content
 * `contentView` which is triggered in a synchronous fashion
 * as soon as the belonging content is rendered.
 * A use case where the tracking event is triggered by a
 * user event should normally be save.
 */
export const AsyncPageTrackingContextProvider: FC<React.PropsWithChildren> = ({
  children,
}) => {
  const registeredListeners = useRef(
    new Set<PageTrackingContextChangeListener>(),
  )

  const trackingContext =
    useRef<LoadableTrackingContext<TrackingPageContextValue> | null>(null)

  const addChangeListener = useCallback(
    (
      listener: PageTrackingContextChangeListener,
      getInitialState?: boolean,
    ) => {
      registeredListeners.current.add(listener)
      if (getInitialState && trackingContext.current) {
        listener(trackingContext.current)
      }
    },
    [registeredListeners],
  )
  const removeChangeListener = useCallback(
    (listener: PageTrackingContextChangeListener) => {
      registeredListeners.current.delete(listener)
    },
    [registeredListeners],
  )
  const update = useCallback(
    (context: LoadableTrackingContext<TrackingPageContextValue>) => {
      trackingContext.current = context

      for (const listener of registeredListeners.current) {
        listener(context)
      }
    },
    [registeredListeners, trackingContext],
  )

  const asyncPageTrackingContextValue: AsyncTrackingPageContextValue = useMemo(
    () => ({
      addChangeListener,
      removeChangeListener,
      update,
      contextRef: trackingContext,
    }),
    [addChangeListener, removeChangeListener, update, trackingContext],
  )

  return (
    <AsyncPageTrackingContext.Provider value={asyncPageTrackingContextValue}>
      {children}
    </AsyncPageTrackingContext.Provider>
  )
}

export const useAsyncPageTrackingContextUpdater = (
  context: LoadableTrackingContext<TrackingPageContextValue> | null,
) => {
  const { update } = useContext(AsyncPageTrackingContext)

  useEffect(() => {
    if (context !== null) {
      update(context)
    }
  }, [context, update])
}

export const useAsyncPageTrackingContextValue = (getInitialState?: boolean) => {
  const [pageTrackingContext, setPageTrackingContext] =
    useState<LoadableTrackingContext<TrackingPageContextValue> | null>(null)
  const { addChangeListener, removeChangeListener } = useContext(
    AsyncPageTrackingContext,
  )

  useEffect(() => {
    addChangeListener(setPageTrackingContext, getInitialState)
    return () => removeChangeListener(setPageTrackingContext)
  }, [
    addChangeListener,
    removeChangeListener,
    setPageTrackingContext,
    getInitialState,
  ])

  return pageTrackingContext
}
